/* eslint-disable no-restricted-globals */
import { useEffect, useMemo, useRef, useState } from "react";
import { Bot, Mic, MicOff, Send, X, Loader2, Sparkles, FileSpreadsheet } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Simple markdown-ish renderer: **bold**, line breaks, tables via <pre>.
function renderText(t) {
  if (!t) return null;
  // Split by lines to preserve structure
  return t.split("\n").map((line, i) => {
    // Bold **x**
    const parts = line.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
      /^\*\*[^*]+\*\*$/.test(p)
        ? <strong key={j} className="font-semibold text-orange-700">{p.replace(/^\*\*|\*\*$/g, "")}</strong>
        : <span key={j}>{p}</span>
    );
    return (
      <div key={i} className={line.trim() === "" ? "h-2" : ""}>
        {parts}
      </div>
    );
  });
}

function newSessionId() {
  return "adm-" + Math.random().toString(36).slice(2, 10) + "-" + Date.now().toString(36);
}

const REPORT_URL_RE = /\S*\/api\/admin\/reports\/download\/([A-Za-z0-9_-]+\.xlsx)\S*/g;

// Ambil nama file laporan dari teks AI, lalu buang link-nya (markdown maupun URL polos).
function extractReports(text) {
  if (!text) return { text, files: [] };
  const files = [...new Set([...text.matchAll(REPORT_URL_RE)].map((m) => m[1]))];
  if (!files.length) return { text, files };
  const clean = text
    .replace(/\[[^\]]*\]\s*\(\s*\S*\/api\/admin\/reports\/download\/[^)\s]+\s*\)/g, "")
    .replace(REPORT_URL_RE, "")
    .replace(/\*\*\s*\*\*|`\s*`|\(\s*\)|\[\s*\]/g, "")
    .split("\n").filter((l) => /[\p{L}\p{N}]/u.test(l) || l.trim() === "").join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return { text: clean, files };
}

function ReportDownloadButton({ filename, idx }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const blob = await api.downloadAdminReport(filename);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      if (e?.response?.status === 404) toast.error("Link laporan sudah kedaluwarsa (10 menit). Minta laporan baru ke AI.");
      else toast.error("Gagal mengunduh laporan");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" onClick={download} disabled={busy}
      className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-semibold px-3 py-1.5 transition-colors"
      data-testid={`admin-ai-download-excel-${idx}`}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
      Unduh Excel
    </button>
  );
}

const SUGGESTIONS = [
  "Berapa unit AC dikerjakan hari ini?",
  "Kapasitor 30uf terpakai bulan ini",
  "Rekap penagihan minggu ini",
  "Komisi armada bulan ini",
];

export function AdminAiChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);   // {role: 'user'|'ai', text}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [listening, setListening] = useState(false);
  const [sessionId] = useState(() => {
    try {
      const cached = sessionStorage.getItem("admin_ai_session_id");
      if (cached) return cached;
    } catch {}
    const nid = newSessionId();
    try { sessionStorage.setItem("admin_ai_session_id", nid); } catch {}
    return nid;
  });
  const scrollRef = useRef(null);
  const recRef = useRef(null);

  // Web Speech API — cek support
  const SpeechRecognition = useMemo(
    () => window.SpeechRecognition || window.webkitSpeechRecognition,
    []
  );
  const voiceSupported = !!SpeechRecognition;

  useEffect(() => {
    if (!open) return;
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const startListening = () => {
    if (!voiceSupported) {
      toast.warning("Browser Anda tidak mendukung voice input. Coba Chrome/Edge.");
      return;
    }
    try {
      const rec = new SpeechRecognition();
      rec.lang = "id-ID";
      rec.continuous = false;
      rec.interimResults = true;
      rec.onstart = () => setListening(true);
      rec.onresult = (ev) => {
        const t = Array.from(ev.results)
          .map((r) => r[0].transcript)
          .join(" ");
        setInput(t);
      };
      rec.onerror = (ev) => {
        setListening(false);
        toast.error(`Voice error: ${ev.error}`);
      };
      rec.onend = () => setListening(false);
      recRef.current = rec;
      rec.start();
    } catch (e) {
      setListening(false);
      toast.error("Gagal aktifkan voice input");
    }
  };

  const stopListening = () => {
    try { recRef.current?.stop(); } catch {}
    setListening(false);
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setSending(true);
    try {
      const r = await api.adminAiChat(sessionId, msg);
      setMessages((m) => [...m, { role: "ai", text: r.reply || "-", tools: r.tool_calls || [] }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Gagal";
      setMessages((m) => [...m, { role: "ai", text: `⚠️ ${detail}`, error: true }]);
    } finally {
      setSending(false);
    }
  };

  const handleKey = (ev) => {
    if (ev.key === "Enter" && !ev.shiftKey) {
      ev.preventDefault();
      send();
    }
  };

  if (!open) {
    return (
      <button
        data-testid="admin-ai-fab"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-xl ring-2 ring-white hover:scale-105 transition grid place-items-center"
        title="Admin AI Chat"
      >
        <Bot className="w-6 h-6" />
        <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-emerald-500 ring-2 ring-white" />
      </button>
    );
  }

  return (
    <div
      data-testid="admin-ai-panel"
      className="fixed bottom-5 right-5 z-40 w-[380px] max-w-[calc(100vw-24px)] h-[560px] max-h-[calc(100vh-40px)] rounded-2xl bg-white shadow-2xl border border-zinc-200 flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-3 flex items-center gap-3">
        <div className="h-8 w-8 rounded-full bg-white/20 grid place-items-center">
          <Bot className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">Admin AI</div>
          <div className="text-[11px] opacity-90 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Baca data ops (WO, billing, komisi)
          </div>
        </div>
        <button
          data-testid="admin-ai-close"
          onClick={() => setOpen(false)}
          className="h-8 w-8 rounded-lg hover:bg-white/20 grid place-items-center"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4 space-y-3 bg-zinc-50"
        data-testid="admin-ai-messages"
      >
        {messages.length === 0 && (
          <div className="text-center py-8">
            <div className="w-12 h-12 mx-auto rounded-full bg-orange-100 grid place-items-center mb-3">
              <Sparkles className="w-6 h-6 text-orange-600" />
            </div>
            <div className="text-sm font-medium text-zinc-700">Halo Kak Admin 👋</div>
            <div className="text-xs text-zinc-500 mt-1 mb-4">
              Tanya apa saja tentang data ops. Contoh:
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  data-testid={`admin-ai-suggest-${s.slice(0, 10).replace(/\s+/g, "-")}`}
                  onClick={() => send(s)}
                  className="text-[11px] px-2.5 py-1 rounded-full bg-white border border-zinc-200 hover:border-orange-400 hover:text-orange-700 transition"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => {
          const rep = m.role === "ai" ? extractReports(m.text) : { text: m.text, files: [] };
          return (
          <div
            key={i}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            data-testid={`admin-ai-msg-${m.role}-${i}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${
                m.role === "user"
                  ? "bg-orange-600 text-white rounded-br-sm"
                  : m.error
                  ? "bg-red-50 text-red-700 border border-red-200 rounded-bl-sm"
                  : "bg-white text-zinc-800 shadow-sm border border-zinc-100 rounded-bl-sm"
              }`}
            >
              <div className="whitespace-pre-wrap break-words">{renderText(rep.text)}</div>
              {rep.files.map((f, k) => <ReportDownloadButton key={f} filename={f} idx={`${i}-${k}`} />)}
              {m.tools?.length > 0 && (
                <div className="mt-1.5 pt-1.5 border-t border-zinc-100 text-[10px] text-zinc-400">
                  🔧 {m.tools.map((t) => t.name).join(", ")}
                </div>
              )}
            </div>
          </div>
          );
        })}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white shadow-sm border border-zinc-100 rounded-2xl rounded-bl-sm px-3.5 py-2 flex items-center gap-2 text-sm text-zinc-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Mengetik…
            </div>
          </div>
        )}
      </div>

      {/* Input bar */}
      <div className="border-t border-zinc-200 bg-white p-2.5 flex items-end gap-2">
        <button
          data-testid="admin-ai-mic"
          onClick={listening ? stopListening : startListening}
          disabled={!voiceSupported}
          className={`h-10 w-10 rounded-xl grid place-items-center flex-shrink-0 transition ${
            listening
              ? "bg-red-500 text-white animate-pulse"
              : voiceSupported
              ? "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
              : "bg-zinc-50 text-zinc-300 cursor-not-allowed"
          }`}
          title={voiceSupported ? "Push to talk (Bahasa Indonesia)" : "Voice tidak didukung browser ini"}
        >
          {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </button>
        <textarea
          data-testid="admin-ai-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          rows={1}
          placeholder={listening ? "Mendengarkan…" : "Tanya data ops…"}
          disabled={sending}
          className="flex-1 resize-none px-3 py-2.5 rounded-xl bg-zinc-50 border border-zinc-200 focus:outline-none focus:border-orange-400 text-sm max-h-24 leading-tight"
        />
        <button
          data-testid="admin-ai-send"
          onClick={() => send()}
          disabled={sending || !input.trim()}
          className="h-10 w-10 rounded-xl bg-orange-600 text-white grid place-items-center flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-orange-700 transition"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default AdminAiChat;
